import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { signalsAPI } from '../services/api'
import api from '../services/api'

interface Anomaly {
  type: string
  severity: number
  headline: string
  direction_symbol: string
  detail?: string
}

interface MaxPainAnalysis {
  max_pain_strike?: number
  spot_vs_max_pain?: number
  expected_drift?: string
  expiry_pressure?: string
  signal?: string
}

interface PcrAnalysis {
  pcr?: number
  pcr_baseline?: number
  day_change_pct?: number
  pcr_regime?: string
  trend?: string
  composite?: string
  signal?: string
}

interface V8Confirmation {
  confirmed: boolean
  match_found: boolean
  staleness_minutes: number | null
  direction_conviction: string | null
  anomaly_types: string[]
  high_value_confirmation: string[]
}

interface Signal {
  id: string
  symbol: string
  signal_type: string
  anomaly_score: number
  generated_at: string
  details: {
    spot: number
    atm_iv: number
    pcr: number
    anomalies: Anomaly[]
    strategy_hint?: string
    direction_bias?: string
    direction_conviction?: string
    direction_evidence?: string[]
    market_regime?: string
    verdict?: string
    verdict_reason?: string
    verdict_evidence?: string[]
    max_pain_analysis?: MaxPainAnalysis
    pcr_analysis?: PcrAnalysis
    // R14 fields (signal_type starts with "R14_") -- see isR14Signal()
    ticker?: string
    direction?: string          // "bull" | "bear" | "mixed"
    xgb_prob?: number           // 0-1 probability
    xgb_lbl?: string            // "HIGH" | "MED"
    tier?: string               // "fired" | "primed" | "building"
    score?: number
    price_eod?: number
    sl_eod?: number
    t1_eod?: number
    rsi?: number
    v8_confirmation?: V8Confirmation
  }
}

const isR14Signal = (signal: Signal) => signal.signal_type?.startsWith('R14_')

interface AccuracyStats {
  total_signals: number
  moved_1pct_30m: number
  moved_2pct_30m: number
  avg_move_30m: number
  low_sample?: boolean
}

interface ConvictionStats {
  total: number
  hit_rate: number | null
  avg_move: number | null
  low_sample: boolean
}

interface AccuracyResponse {
  magnitude?: { [key: string]: AccuracyStats }
  direction?: {
    total_calls: number
    hit_rate: number | null
    low_sample: boolean
  }
  conviction?: { [key: string]: ConvictionStats }
  total_outcomes?: number
  window_days?: number
}

// Keep old interface for backward compat
interface AccuracyData {
  [key: string]: AccuracyStats
}

type Tab = 'live' | 'history' | 'accuracy'

const getStyles = (isMobile: boolean): Record<string, React.CSSProperties> => ({
  container: {
    background: '#0a0e1a',
    minHeight: '100vh',
    color: '#fff',
    overflowX: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: isMobile ? '12px 16px' : '16px 24px',
    borderBottom: '1px solid #1f2937',
    background: '#0d1117',
  },
  logo: { color: '#00d4ff', fontSize: isMobile ? 16 : 20, fontWeight: 700 },
  planBadge: {
    marginLeft: 8,
    background: '#1f2937',
    color: '#00d4ff',
    padding: '2px 8px',
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16 },
  updateTime: {
    color: '#4b5563',
    fontSize: 11,
    display: isMobile ? 'none' : 'block',
  },
  logoutBtn: {
    background: 'transparent',
    color: '#6b7280',
    border: '1px solid #374151',
    padding: isMobile ? '4px 10px' : '6px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12,
  },
  telegramBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: isMobile ? '10px 16px' : '10px 24px',
    background: '#0d1117',
    borderTop: '1px solid #1f2937',
    borderBottom: '1px solid #1f2937',
    flexWrap: 'wrap' as const,
    gap: 8,
  },
  telegramBarText: {
    color: '#9ca3af',
    fontSize: isMobile ? 12 : 13,
  },
  telegramBtn: {
    background: '#00d4ff',
    color: '#000',
    border: 'none',
    padding: isMobile ? '6px 14px' : '7px 18px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: isMobile ? 12 : 13,
    fontWeight: 700,
    whiteSpace: 'nowrap' as const,
  },
  statsBar: {
    display: 'flex',
    borderBottom: '1px solid #1f2937',
    background: '#0d1117',
    overflowX: 'auto',
  },
  stat: {
    flex: 1,
    padding: isMobile ? '12px 8px' : '16px 24px',
    textAlign: 'center',
    borderRight: '1px solid #1f2937',
    minWidth: isMobile ? 70 : 'auto',
  },
  statValue: {
    display: 'block',
    fontSize: isMobile ? 18 : 24,
    fontWeight: 700,
    color: '#00d4ff',
  },
  statLabel: {
    display: 'block',
    fontSize: isMobile ? 9 : 11,
    color: '#6b7280',
    marginTop: 2,
  },
  tabs: {
    display: 'flex',
    padding: isMobile ? '0 8px' : '0 24px',
    borderBottom: '1px solid #1f2937',
    background: '#0d1117',
    overflowX: 'auto',
  },
  tab: {
    padding: isMobile ? '12px 12px' : '14px 24px',
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    fontSize: isMobile ? 12 : 14,
    borderBottom: '2px solid transparent',
    whiteSpace: 'nowrap' as const,
  },
  tabActive: { color: '#00d4ff', borderBottom: '2px solid #00d4ff' },
  content: { padding: isMobile ? 12 : 24 },
  grid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: isMobile ? 12 : 16,
  },
  card: {
    background: '#111827',
    border: '1px solid #1f2937',
    borderRadius: 12,
    padding: isMobile ? 16 : 20,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  symbol: { fontSize: isMobile ? 18 : 20, fontWeight: 700, color: '#fff' },
  scoreBadge: {
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 700,
    color: '#000',
  },
  cardMeta: {
    display: 'flex',
    gap: isMobile ? 8 : 12,
    marginBottom: 16,
    flexWrap: 'wrap' as const,
  },
  metaItem: { color: '#6b7280', fontSize: isMobile ? 12 : 13 },
  anomalyRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    padding: '10px 0',
    borderTop: '1px solid #1f2937',
  },
  dirSymbol: { fontSize: isMobile ? 16 : 20, minWidth: 20 },
  anomalyType: { color: '#00d4ff', fontSize: 11, fontWeight: 700, marginBottom: 2 },
  anomalyHeadline: { color: '#e5e7eb', fontSize: isMobile ? 12 : 13 },
  anomalyDetail: { color: '#6b7280', fontSize: 11, marginTop: 4 },
  hint: {
    background: '#1f2937',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: isMobile ? 12 : 13,
    color: '#9ca3af',
    marginTop: 12,
  },
  timestamp: { color: '#374151', fontSize: 11, marginTop: 12 },
  empty: {
    textAlign: 'center' as const,
    padding: '60px 20px',
    color: '#6b7280',
    fontSize: 16,
  },
  emptyIcon: { fontSize: 48, marginBottom: 16 },
  emptyHint: { fontSize: 13, color: '#4b5563', marginTop: 8 },
  locked: { textAlign: 'center' as const, padding: '60px 20px' },
  lockIcon: { fontSize: 48, marginBottom: 16 },
  lockTitle: { fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 8 },
  lockText: { color: '#6b7280', marginBottom: 24 },
  upgradeBtn: {
    background: '#00d4ff',
    color: '#000',
    border: 'none',
    padding: '12px 32px',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
  },
  historyControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap' as const,
  },
  historyLabel: { color: '#6b7280', fontSize: 13 },
  dayBtn: {
    background: '#1f2937',
    color: '#6b7280',
    border: '1px solid #374151',
    padding: '4px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12,
  },
  dayBtnActive: {
    background: '#00d4ff',
    color: '#000',
    border: '1px solid #00d4ff',
  },
  historyTable: {
    background: '#111827',
    borderRadius: 12,
    overflow: 'hidden',
    overflowX: 'auto' as const,
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '75px 85px 1fr 55px' : '120px 120px 1fr 80px 100px 80px',
    padding: '12px 16px',
    background: '#1f2937',
    gap: 8,
  },
  th: {
    color: '#6b7280',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '75px 85px 1fr 55px' : '120px 120px 1fr 80px 100px 80px',
    padding: '12px 16px',
    borderTop: '1px solid #1f2937',
    gap: 8,
    alignItems: 'center',
  },
  td: { color: '#e5e7eb', fontSize: isMobile ? 12 : 13 },
  accuracyGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16,
  },
  accuracyCard: {
    background: '#111827',
    border: '1px solid #1f2937',
    borderRadius: 12,
    padding: 20,
  },
  accuracyType: { color: '#00d4ff', fontWeight: 700, fontSize: 14, marginBottom: 16 },
  accuracyStats: { display: 'flex', justifyContent: 'space-between', marginBottom: 16 },
  accuracyStat: { textAlign: 'center' as const },
  accuracyValue: { display: 'block', fontSize: 24, fontWeight: 700, color: '#fff' },
  accuracyLabel: { display: 'block', fontSize: 11, color: '#6b7280', marginTop: 4 },
  progressBar: { height: 6, background: '#1f2937', borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 3, transition: 'width 0.3s ease' },
  disclaimer: {
    textAlign: 'center' as const,
    padding: '16px',
    color: '#374151',
    fontSize: 11,
    borderTop: '1px solid #1f2937',
  },
})

export default function Dashboard() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [signals, setSignals] = useState<Signal[]>([])
  const [history, setHistory] = useState<Signal[]>([])
  const [accuracyData, setAccuracyData] = useState<AccuracyResponse>({})
  const [plan, setPlan] = useState('basic')
  const [nextScan, setNextScan] = useState('')
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState('')
  const [activeTab, setActiveTab] = useState<Tab>('live')
  const [sourceFilter, setSourceFilter] = useState<'all' | 'r14' | 'v8'>('all')

  const matchesSourceFilter = (sig: Signal) =>
    sourceFilter === 'all' ||
    (sourceFilter === 'r14') === isR14Signal(sig)
  const [historyDays, setHistoryDays] = useState(5)
  const [telegramConnected, setTelegramConnected] = useState(false)
  const [telegramConnecting, setTelegramConnecting] = useState(false)
  const [telegramChecked, setTelegramChecked] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const s = getStyles(isMobile)

  const fetchSignals = async () => {
    try {
      const res = await signalsAPI.getLatest()
      setSignals(res.data.signals || [])
      setPlan(res.data.plan || 'basic')
      setNextScan(res.data.next_scan || '')
      setLastUpdate(new Date().toLocaleTimeString('en-IN'))
    } catch (err) {
      console.error('Failed to fetch signals', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchHistory = async (days: number = 5) => {
    try {
      const res = await signalsAPI.getHistory(days)
      setHistory(res.data.signals || [])
    } catch (err) {
      console.error('Failed to fetch history', err)
    }
  }

  const fetchAccuracy = async () => {
    try {
      const res = await signalsAPI.getAccuracy()
      setAccuracyData(res.data || {})
    } catch (err) {
      console.error('Failed to fetch accuracy', err)
    }
  }

  useEffect(() => {
    fetchSignals()
    const interval = setInterval(fetchSignals, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (activeTab === 'history') fetchHistory(historyDays)
    if (activeTab === 'accuracy') fetchAccuracy()
  }, [activeTab, historyDays])

  // Check Telegram connection status once on mount
  useEffect(() => {
    const checkTelegramStatus = async () => {
      try {
        const res = await api.get('/api/telegram/status')
        setTelegramConnected(!!res.data.connected)
      } catch (err) {
        console.error('Failed to check Telegram status', err)
      } finally {
        setTelegramChecked(true)
      }
    }
    checkTelegramStatus()
  }, [])

  const connectTelegram = async () => {
    setTelegramConnecting(true)
    try {
      const res = await api.post('/api/telegram/generate-link-code')
      const deepLink = res.data.deep_link
      if (deepLink) {
        window.open(deepLink, '_blank')
      }

      // Poll for up to 2 minutes (15-min code expiry, but users act fast or not at all)
      let attempts = 0
      const maxAttempts = 40 // 40 * 3s = 2 minutes
      const poll = setInterval(async () => {
        attempts += 1
        try {
          const statusRes = await api.get('/api/telegram/status')
          if (statusRes.data.connected) {
            setTelegramConnected(true)
            setTelegramConnecting(false)
            clearInterval(poll)
          }
        } catch (err) {
          console.error('Telegram status poll failed', err)
        }
        if (attempts >= maxAttempts) {
          setTelegramConnecting(false)
          clearInterval(poll)
        }
      }, 3000)
    } catch (err) {
      console.error('Failed to generate Telegram link', err)
      setTelegramConnecting(false)
    }
  }

  const logout = () => {
    localStorage.clear()
    navigate('/login')
  }

  const severityColor = (score: number) => {
    if (score >= 7) return '#ef4444'
    if (score >= 4) return '#f59e0b'
    return '#00d4ff'
  }

  const scoreDisplay = (score: number) => `${Math.min(score, 10)}/10`

  // R14 uses a 0-100 probability-derived score (xgb_prob * 100), not the
  // old 0-10 anomaly-severity scale -- scoreDisplay/severityColor above
  // would otherwise clip every R14 signal to a flat "10/10".
  const r14ScoreColor = (probPct: number) => {
    if (probPct >= 70) return '#ef4444'
    if (probPct >= 65) return '#f59e0b'
    return '#00d4ff'
  }
  const r14ScoreDisplay = (probPct: number) => `${probPct.toFixed(1)}%`

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={s.logo}>AITradeMinds</span>
          <span style={s.planBadge}>{plan.toUpperCase()}</span>
        </div>
        
        <div style={{ display: 'flex', gap: 12, marginLeft: 16 }}>
          <span style={{ color: '#6b7280', fontSize: 13, cursor: 'pointer' }}
            onClick={() => navigate('/market')}>
            📊 Market
          </span>
          <span style={{ color: '#6b7280', fontSize: 13, cursor: 'pointer' }}
            onClick={() => navigate('/screener')}>
            🔍 Screener
          </span>
        </div>
        <div style={s.headerRight}>
          {lastUpdate && <span style={s.updateTime}>Updated {lastUpdate}</span>}
          <button style={s.logoutBtn} onClick={logout}>Logout</button>
        </div>
      </div>

      {/* Telegram connect bar — only shown once status check completes */}
      {telegramChecked && !telegramConnected && (
        <div style={s.telegramBar}>
          <span style={s.telegramBarText}>
            📱 Get instant alerts on Telegram
          </span>
          <button
            style={s.telegramBtn}
            onClick={connectTelegram}
            disabled={telegramConnecting}
          >
            {telegramConnecting ? 'Waiting for connection...' : 'Connect Telegram'}
          </button>
        </div>
      )}
      {telegramChecked && telegramConnected && (
        <div style={{ ...s.telegramBar, background: '#0f2d1a', borderColor: '#14532d' }}>
          <span style={{ ...s.telegramBarText, color: '#22c55e' }}>
            ✅ Telegram connected — you'll receive signal alerts here
          </span>
        </div>
      )}

      {/* Stats bar */}
      <div style={s.statsBar}>
        <div style={s.stat}>
          <span style={{ ...s.statValue, color: '#00d4ff' }}>
            {signals.filter(sig => isR14Signal(sig)).length}
          </span>
          <span style={s.statLabel}>R14 Signals</span>
        </div>
        <div style={s.stat}>
          <span style={s.statValue}>
            {signals.filter(sig => !isR14Signal(sig)).length}
          </span>
          <span style={s.statLabel}>V8 Anomalies</span>
        </div>
        <div style={s.stat}>
          <span style={s.statValue}>203</span>
          <span style={s.statLabel}>Scanned</span>
        </div>
        <div style={s.stat}>
          <span style={s.statValue}>{nextScan || 'Closed'}</span>
          <span style={s.statLabel}>Next Scan</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {(['live', 'history', 'accuracy'] as Tab[]).map(tab => (
          <button
            key={tab}
            style={{ ...s.tab, ...(activeTab === tab ? s.tabActive : {}) }}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'live' && (isMobile ? '📡 Live' : '📡 Live Signals')}
            {tab === 'history' && (isMobile ? '📋 History' : '📋 History')}
            {tab === 'accuracy' && (isMobile ? '🎯 Accuracy' : '🎯 Accuracy')}
          </button>
        ))}
      </div>

      {/* Source filter -- applies to Live and History tabs, keeps R14 and
          v8 visually/functionally separate rather than one blended feed */}
      {(activeTab === 'live' || activeTab === 'history') && (
        <div style={{ display: 'flex', gap: 8, padding: '10px 16px 0' }}>
          {(['all', 'r14', 'v8'] as const).map(src => (
            <button
              key={src}
              onClick={() => setSourceFilter(src)}
              style={{
                padding: '6px 14px',
                borderRadius: 16,
                fontSize: 12,
                fontWeight: 600,
                border: '1px solid ' + (sourceFilter === src ? '#00d4ff' : '#1f2937'),
                background: sourceFilter === src ? '#0a2530' : 'transparent',
                color: sourceFilter === src ? '#00d4ff' : '#6b7280',
                cursor: 'pointer',
              }}
            >
              {src === 'all' ? 'All' : src === 'r14' ? 'R14' : 'V8'}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div style={s.content}>

        {/* LIVE TAB */}
        {activeTab === 'live' && (
          loading ? (
            <div style={s.empty}>Loading signals...</div>
          ) : signals.filter(matchesSourceFilter).length === 0 ? (
            <div style={s.empty}>
              <div style={s.emptyIcon}>📊</div>
              <div>No anomalies detected in this scan</div>
              <div style={s.emptyHint}>Market may be low-conviction or pipeline warming up</div>
              <div style={s.emptyHint}>Next scan at {nextScan}</div>
            </div>
          ) : (
            <div style={s.grid}>
              {signals.filter(matchesSourceFilter).map(signal => (
                <div key={signal.id} style={{
                  ...s.card,
                  borderLeft: signal.details?.verdict === 'STRONG_BULLISH' ? '3px solid #22c55e' :
                              signal.details?.verdict === 'STRONG_BEARISH' ? '3px solid #ef4444' :
                              signal.details?.verdict === 'BULLISH' ? '3px solid #86efac' :
                              signal.details?.verdict === 'BEARISH' ? '3px solid #fca5a5' :
                              signal.details?.verdict === 'CONTEXT_BULLISH' ? '3px solid #475569' :
                              signal.details?.verdict === 'CONTEXT_BEARISH' ? '3px solid #475569' :
                              signal.details?.verdict === 'CONFLICTING' ? '3px solid #f59e0b' :
                              '1px solid #1f2937',
                }}>
                  {/* Header: symbol + score */}
                  <div style={s.cardHeader}>
                    <span style={s.symbol}>{signal.symbol}</span>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {signal.details?.direction_conviction &&
                       signal.details.direction_conviction !== 'NONE' && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 6px',
                          borderRadius: 10,
                          background:
                            signal.details.direction_conviction === 'HIGH' ? '#14532d' :
                            signal.details.direction_conviction === 'FRESH' ? '#1e3a5f' :
                            signal.details.direction_conviction === 'STALE' ? '#451a03' : '#1f2937',
                          color:
                            signal.details.direction_conviction === 'HIGH' ? '#22c55e' :
                            signal.details.direction_conviction === 'FRESH' ? '#00d4ff' :
                            signal.details.direction_conviction === 'STALE' ? '#f59e0b' : '#6b7280',
                        }}>
                          {signal.details.direction_conviction}
                        </span>
                      )}
                      <span style={{
                        ...s.scoreBadge,
                        background: isR14Signal(signal)
                          ? r14ScoreColor(signal.anomaly_score)
                          : severityColor(signal.anomaly_score),
                      }}>
                        {isR14Signal(signal)
                          ? r14ScoreDisplay(signal.anomaly_score)
                          : scoreDisplay(signal.anomaly_score)}
                      </span>
                    </div>
                  </div>

                  {!isR14Signal(signal) && (
                  <>
                  {/* Verdict bar — most prominent element */}
                  {signal.details?.verdict && signal.details.verdict !== 'MONITOR' && (
                    <div style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      marginBottom: 10,
                      background:
                        signal.details.verdict === 'STRONG_BULLISH'  ? '#052e16' :
                        signal.details.verdict === 'STRONG_BEARISH'  ? '#450a0a' :
                        signal.details.verdict === 'BULLISH'         ? '#0f2d1a' :
                        signal.details.verdict === 'BEARISH'         ? '#2d0f0f' :
                        signal.details.verdict === 'CONTEXT_BULLISH' ? '#1e2431' :
                        signal.details.verdict === 'CONTEXT_BEARISH' ? '#1e2431' :
                        signal.details.verdict === 'CONFLICTING'     ? '#1c1208' : '#111827',
                      borderLeft: '3px solid ' + (
                        signal.details.verdict === 'CONTEXT_BULLISH' ? '#64748b' :
                        signal.details.verdict === 'CONTEXT_BEARISH' ? '#64748b' :
                        signal.details.verdict === 'CONFLICTING'     ? '#f59e0b' :
                        signal.details.verdict.includes('BULLISH')   ? '#22c55e' :
                        signal.details.verdict.includes('BEARISH')   ? '#ef4444' : '#f59e0b'
                      ),
                    }}>
                      <div style={{
                        fontSize: 13, fontWeight: 700,
                        color:
                          signal.details.verdict === 'CONTEXT_BULLISH' ? '#94a3b8' :
                          signal.details.verdict === 'CONTEXT_BEARISH' ? '#94a3b8' :
                          signal.details.verdict === 'CONFLICTING'     ? '#f59e0b' :
                          signal.details.verdict.includes('BULLISH')   ? '#22c55e' :
                          signal.details.verdict.includes('BEARISH')   ? '#ef4444' : '#9ca3af',
                      }}>
                        {signal.details.verdict === 'STRONG_BULLISH'  ? '▲▲ STRONG BULLISH' :
                         signal.details.verdict === 'STRONG_BEARISH'  ? '▼▼ STRONG BEARISH' :
                         signal.details.verdict === 'BULLISH'         ? '▲ BULLISH' :
                         signal.details.verdict === 'BEARISH'         ? '▼ BEARISH' :
                         signal.details.verdict === 'CONTEXT_BULLISH' ? '△ LEANS BULLISH (unconfirmed)' :
                         signal.details.verdict === 'CONTEXT_BEARISH' ? '▽ LEANS BEARISH (unconfirmed)' :
                         signal.details.verdict === 'CONFLICTING'     ? '⚠ CONFLICTING' :
                         signal.details.verdict}
                      </div>
                      {signal.details.verdict_reason && (
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                          {signal.details.verdict_reason}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Meta: price, IV, PCR trend */}
                  <div style={s.cardMeta}>
                    {signal.details?.spot > 0 && (
                      <span style={s.metaItem}>
                        ₹{signal.details.spot.toLocaleString('en-IN')}
                      </span>
                    )}
                    {signal.details?.atm_iv > 0 && (
                      <span style={s.metaItem}>IV: {signal.details.atm_iv}%</span>
                    )}
                    {signal.details?.pcr > 0 && (
                      <span style={s.metaItem}>
                        PCR: {signal.details.pcr}
                        {signal.details.pcr_analysis?.trend &&
                         signal.details.pcr_analysis.trend !== 'FLAT' && (
                          <span style={{
                            marginLeft: 4, fontSize: 10,
                            color: signal.details.pcr_analysis.trend.includes('RISING')
                              ? '#ef4444' : '#22c55e'
                          }}>
                            {signal.details.pcr_analysis.trend.includes('RISING') ? '▲' : '▼'}
                            {signal.details.pcr_analysis.trend.replace(/_/g, ' ')}
                          </span>
                        )}
                      </span>
                    )}
                  </div>

                  {/* Anomaly details */}
                  {signal.details?.anomalies?.map((a, i) => (
                    <div key={i} style={s.anomalyRow}>
                      <span style={s.dirSymbol}>{a.direction_symbol}</span>
                      <div>
                        <div style={s.anomalyType}>{a.type}</div>
                        <div style={s.anomalyHeadline}>{a.headline}</div>
                        {a.detail && (
                          <div style={s.anomalyDetail}>{a.detail}</div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Strategy hint */}
                  {signal.details?.strategy_hint && (
                    <div style={s.hint}>💡 {signal.details.strategy_hint}</div>
                  )}

                  {/* Max Pain — only when valid (filters applied in engine) */}
                  {signal.details?.max_pain_analysis?.signal && (
                    <div style={{
                      ...s.hint,
                      background: '#1a150a',
                      borderLeft: '3px solid #f59e0b',
                      marginTop: 6,
                      fontSize: isMobile ? 11 : 12,
                    }}>
                      📍 {signal.details.max_pain_analysis.signal}
                    </div>
                  )}

                  {/* PCR context — only show notable regimes or fast trends */}
                  {signal.details?.pcr_analysis?.signal &&
                   (signal.details.pcr_analysis.pcr_regime === 'EXTREME_FEAR' ||
                    signal.details.pcr_analysis.pcr_regime === 'EXTREME_GREED' ||
                    signal.details.pcr_analysis.trend === 'RISING_FAST' ||
                    signal.details.pcr_analysis.trend === 'FALLING_FAST') && (
                    <div style={{
                      ...s.hint,
                      background: '#0a1628',
                      borderLeft: '3px solid #00d4ff',
                      marginTop: 6,
                      fontSize: isMobile ? 11 : 12,
                    }}>
                      📊 {signal.details.pcr_analysis.signal}
                    </div>
                  )}
                  </>
                  )}

                  {/* R14 signal body -- separate from v8's anomaly/verdict rendering above, since R14's payload shape (xgb_prob, direction, entry/SL/target) is entirely different from v8's anomaly-severity model */}
                  {isR14Signal(signal) && (
                    <>
                      {signal.details?.direction && (
                        <div style={{
                          padding: '8px 12px',
                          borderRadius: 8,
                          marginBottom: 10,
                          background: signal.details.direction === 'bull' ? '#0f2d1a' :
                                      signal.details.direction === 'bear' ? '#2d0f0f' : '#1e2431',
                          borderLeft: '3px solid ' + (
                            signal.details.direction === 'bull' ? '#22c55e' :
                            signal.details.direction === 'bear' ? '#ef4444' : '#64748b'
                          ),
                        }}>
                          <div style={{
                            fontSize: 13, fontWeight: 700,
                            color: signal.details.direction === 'bull' ? '#22c55e' :
                                   signal.details.direction === 'bear' ? '#ef4444' : '#9ca3af',
                          }}>
                            {signal.details.direction === 'bull' ? '▲ BULLISH' :
                             signal.details.direction === 'bear' ? '▼ BEARISH' :
                             signal.details.direction?.toUpperCase()}
                            {signal.details.xgb_lbl && (
                              <span style={{ fontSize: 10, marginLeft: 8, color: '#6b7280', fontWeight: 400 }}>
                                confidence: {signal.details.xgb_lbl}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      <div style={s.cardMeta}>
                        {signal.details?.price_eod != null && (
                          <span style={s.metaItem}>₹{signal.details.price_eod.toLocaleString('en-IN')}</span>
                        )}
                        {signal.details?.sl_eod != null && (
                          <span style={s.metaItem}>SL: ₹{signal.details.sl_eod.toLocaleString('en-IN')}</span>
                        )}
                        {signal.details?.t1_eod != null && (
                          <span style={s.metaItem}>T1: ₹{signal.details.t1_eod.toLocaleString('en-IN')}</span>
                        )}
                        {signal.details?.rsi != null && (
                          <span style={s.metaItem}>RSI: {signal.details.rsi}</span>
                        )}
                        {signal.details?.tier && (
                          <span style={s.metaItem}>{signal.details.tier}</span>
                        )}
                      </div>

                      {/* v8 soft-tag confirmation -- informational only, never gates delivery.
                          See r14_tasks.py's find_v8_confirmation() for what "confirmed" means. */}
                      {signal.details?.v8_confirmation?.match_found && (
                        <div style={{
                          ...s.hint,
                          background: signal.details.v8_confirmation.confirmed ? '#0f2d1a' : '#111827',
                          borderLeft: '3px solid ' + (signal.details.v8_confirmation.confirmed ? '#22c55e' : '#374151'),
                          marginTop: 6,
                          fontSize: isMobile ? 11 : 12,
                        }}>
                          {signal.details.v8_confirmation.confirmed ? '✅ V8-confirmed' : 'ℹ️ V8 anomaly seen'}
                          {signal.details.v8_confirmation.anomaly_types.length > 0 &&
                            ` (${signal.details.v8_confirmation.anomaly_types.join(', ')})`}
                          {signal.details.v8_confirmation.staleness_minutes != null &&
                            ` · ${signal.details.v8_confirmation.staleness_minutes.toFixed(0)}m old`}
                        </div>
                      )}
                    </>
                  )}

                  <div style={s.timestamp}>
                    {new Date(signal.generated_at).toLocaleTimeString('en-IN')}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          plan === 'basic' ? (
            <div style={s.locked}>
              <div style={s.lockIcon}>🔒</div>
              <div style={s.lockTitle}>Pro Feature</div>
              <div style={s.lockText}>Signal history requires Pro or Elite plan</div>
    
              <button style={s.upgradeBtn} onClick={() => navigate('/upgrade')}> Upgrade to Pro — ₹999/month
              </button>
            </div>
          ) : (
            <>
              <div style={s.historyControls}>
                <span style={s.historyLabel}>Show last:</span>
                {[1, 3, 5].map(d => (
                  <button
                    key={d}
                    style={{
                      ...s.dayBtn,
                      ...(historyDays === d ? s.dayBtnActive : {})
                    }}
                    onClick={() => setHistoryDays(d)}
                  >
                    {d}d
                  </button>
                ))}
              </div>
              {history.filter(matchesSourceFilter).length === 0 ? (
                <div style={s.empty}>
                  <div style={s.emptyIcon}>📋</div>
                  <div>No historical signals yet</div>
                  <div style={s.emptyHint}>History builds up during market hours</div>
                </div>
              ) : (
                <div style={s.historyTable}>
                  <div style={s.tableHeader}>
                    <span style={s.th}>Time</span>
                    <span style={s.th}>Symbol</span>
                    <span style={s.th}>Signal</span>
                    <span style={s.th}>Score</span>
                    {!isMobile && <span style={s.th}>Price</span>}
                    {!isMobile && <span style={s.th}>PCR</span>}
                  </div>
                  {history.filter(matchesSourceFilter).map(h => (
                    <div key={h.id} style={s.tableRow}>
                      <span style={s.td}>
                        {new Date(h.generated_at).toLocaleTimeString('en-IN', {
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                      <span style={{ ...s.td, color: '#00d4ff', fontWeight: 700 }}>
                        {h.symbol}
                      </span>
                      <span style={{ ...s.td, fontSize: 11 }}>{h.signal_type}</span>
                      <span style={{
                        ...s.td,
                        color: isR14Signal(h) ? r14ScoreColor(h.anomaly_score) : severityColor(h.anomaly_score),
                      }}>
                        {isR14Signal(h) ? r14ScoreDisplay(h.anomaly_score) : scoreDisplay(h.anomaly_score)}
                      </span>
                      {!isMobile && (
                        <span style={s.td}>
                          ₹{h.details?.spot?.toLocaleString('en-IN') || '-'}
                        </span>
                      )}
                      {!isMobile && (
                        <span style={s.td}>{h.details?.pcr || '-'}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )
        )}


        {/* ACCURACY TAB */}
        {activeTab === 'accuracy' && (
          plan === 'basic' ? (
            <div style={s.locked}>
              <div style={s.lockIcon}>🔒</div>
              <div style={s.lockTitle}>Pro Feature</div>
              <div style={s.lockText}>Accuracy analytics requires Pro or Elite plan</div>
              <button style={s.upgradeBtn} onClick={() => navigate('/upgrade')}>
                Upgrade to Pro — ₹999/month
              </button>
            </div>
          ) : (
            <>
              {/* Direction accuracy summary */}
              {accuracyData.direction && (
                <div style={{
                  background: '#111827', border: '1px solid #1f2937',
                  borderRadius: 12, padding: 20, marginBottom: 24,
                  display: 'flex', gap: 32, flexWrap: 'wrap' as const,
                }}>
                  <div>
                    <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>
                      TOTAL DIRECTIONAL CALLS
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>
                      {accuracyData.direction.total_calls}
                    </div>
                    {accuracyData.direction.low_sample && (
                      <div style={{ color: '#f59e0b', fontSize: 11 }}>⚠ Low sample</div>
                    )}
                  </div>
                  <div>
                    <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>
                      DIRECTION HIT RATE (30m)
                    </div>
                    <div style={{
                      fontSize: 28, fontWeight: 700,
                      color: accuracyData.direction.hit_rate != null
                        ? accuracyData.direction.hit_rate >= 60 ? '#22c55e'
                        : accuracyData.direction.hit_rate >= 50 ? '#f59e0b' : '#ef4444'
                        : '#6b7280',
                    }}>
                      {accuracyData.direction.hit_rate != null
                        ? `${accuracyData.direction.hit_rate}%` : '—'}
                    </div>
                  </div>
                  <div style={{ color: '#4b5563', fontSize: 12, alignSelf: 'flex-end' }}>
                    Based on {accuracyData.total_outcomes || 0} outcomes tracked
                  </div>
                </div>
              )}

              {/* Conviction breakdown table */}
              {accuracyData.conviction && Object.keys(accuracyData.conviction).length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ color: '#fff', fontSize: 15, marginBottom: 12, fontWeight: 600 }}>
                    Direction Accuracy by Conviction
                  </h3>
                  <div style={s.historyTable}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '110px 60px 80px' : '140px 80px 90px 90px 130px',
                      padding: '12px 16px', background: '#1f2937', gap: 8,
                    }}>
                      {['Conviction', 'Signals', 'Hit Rate',
                        ...(!isMobile ? ['Avg Move', 'Status'] : [])
                      ].map(h => (
                        <span key={h} style={s.th}>{h}</span>
                      ))}
                    </div>
                    {Object.entries(accuracyData.conviction).map(([conv, stats]) => (
                      <div key={conv} style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '110px 60px 80px' : '140px 80px 90px 90px 130px',
                        padding: '12px 16px', borderTop: '1px solid #1f2937',
                        gap: 8, alignItems: 'center',
                      }}>
                        <span style={{
                          fontSize: 13, fontWeight: 700,
                          color: conv === 'HIGH' ? '#22c55e'
                            : conv === 'FRESH' ? '#00d4ff'
                            : conv === 'STALE' ? '#f59e0b'
                            : '#ef4444',
                        }}>
                          {conv === 'HIGH' ? '🟢 HIGH'
                            : conv === 'FRESH' ? '🔵 FRESH'
                            : conv === 'STALE' ? '🟡 STALE'
                            : '🔴 CONTRADICTING'}
                        </span>
                        <span style={s.td}>{stats.total}</span>
                        <span style={{
                          ...s.td,
                          color: stats.hit_rate != null
                            ? stats.hit_rate >= 60 ? '#22c55e'
                            : stats.hit_rate >= 50 ? '#f59e0b' : '#ef4444'
                            : '#6b7280',
                          fontWeight: 700,
                        }}>
                          {stats.hit_rate != null ? `${stats.hit_rate}%` : '—'}
                        </span>
                        {!isMobile && (
                          <span style={s.td}>
                            {stats.avg_move != null ? `${stats.avg_move}%` : '—'}
                          </span>
                        )}
                        {!isMobile && (
                          <span style={{ ...s.td, fontSize: 11, color: '#6b7280' }}>
                            {stats.low_sample ? '⚠ Low sample' : '✓ Sufficient'}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <p style={{ color: '#374151', fontSize: 11, marginTop: 8 }}>
                    HIGH = both morning & 1-hr flow agree. STALE = morning signal, flow paused.
                    FRESH = new flow detected. CONTRADICTING = signals conflict, avoid.
                  </p>
                </div>
              )}

              {/* Magnitude accuracy per signal type */}
              {accuracyData.magnitude && Object.keys(accuracyData.magnitude).length > 0 ? (
                <div>
                  <h3 style={{ color: '#fff', fontSize: 15, marginBottom: 12, fontWeight: 600 }}>
                    Magnitude Accuracy by Signal Type
                  </h3>
                  <div style={s.accuracyGrid}>
                    {Object.entries(accuracyData.magnitude).map(([type, stats]) => (
                      <div key={type} style={s.accuracyCard}>
                        <div style={s.accuracyType}>{type}</div>
                        {stats.low_sample && (
                          <div style={{ color: '#f59e0b', fontSize: 11, marginBottom: 8 }}>
                            ⚠ Low sample (n={stats.total_signals}) — not yet reliable
                          </div>
                        )}
                        <div style={s.accuracyStats}>
                          <div style={s.accuracyStat}>
                            <span style={s.accuracyValue}>{stats.total_signals}</span>
                            <span style={s.accuracyLabel}>Total</span>
                          </div>
                          <div style={s.accuracyStat}>
                            <span style={{
                              ...s.accuracyValue,
                              fontSize: 20,
                              color: stats.moved_2pct_30m >= 50 ? '#22c55e' : '#f59e0b',
                            }}>
                              {stats.moved_2pct_30m}%
                            </span>
                            <span style={s.accuracyLabel}>Moved 2%+ in 30m</span>
                          </div>
                          <div style={s.accuracyStat}>
                            <span style={{ ...s.accuracyValue, fontSize: 20 }}>
                              {stats.avg_move_30m}%
                            </span>
                            <span style={s.accuracyLabel}>Avg Move</span>
                          </div>
                        </div>
                        <div style={s.progressBar}>
                          <div style={{
                            ...s.progressFill,
                            width: `${stats.moved_2pct_30m}%`,
                            background: stats.moved_2pct_30m >= 50 ? '#22c55e' : '#f59e0b',
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={s.empty}>
                  <div style={s.emptyIcon}>🎯</div>
                  <div>Accuracy data building up</div>
                  <div style={s.emptyHint}>
                    Outcome tracking runs every 15 mins during market hours.
                    First results appear after Tuesday's market close.
                  </div>
                </div>
              )}
            </>
          )
        )}

      </div>

      <div style={s.disclaimer}>
        ⚠️ Research intelligence only. Not SEBI registered investment advice.
      </div>
    </div>
  )
}